"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseSvgPathToLineChains = parseSvgPathToLineChains;
exports.parsePinPathData = parsePinPathData;
const SVG_TOKEN_RE = /[MZLAChvzlac]|[-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?/g;
class PathTokenizer {
    tokens;
    cursor = 0;
    constructor(raw) {
        this.tokens = raw.match(SVG_TOKEN_RE) ?? [];
    }
    get hasMore() {
        return this.cursor < this.tokens.length;
    }
    take() {
        return this.tokens[this.cursor++] ?? "";
    }
    peekNumeric() {
        const t = this.tokens[this.cursor];
        if (!t)
            return false;
        const c = t.charCodeAt(0);
        return c === 45 || c === 43 || c === 46 || (c >= 48 && c <= 57);
    }
    float() {
        const raw = this.take();
        const v = parseFloat(raw);
        if (isNaN(v))
            throw new Error(`Expected number, got '${raw}'`);
        return v;
    }
    xy() {
        return { x: this.float(), y: this.float() };
    }
}
function isNumericChar(code) {
    return code === 45 || code === 43 || code === 46 || (code >= 48 && code <= 57);
}
function solveArcCenter(p1, p2, rx, large, sweep) {
    const mx = (p1.x + p2.x) / 2;
    const my = (p1.y + p2.y) / 2;
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const chordHalf = Math.sqrt(dx * dx + dy * dy) / 2;
    const arm = Math.sqrt(Math.max(0, rx * rx - chordHalf * chordHalf));
    const perpLen = Math.sqrt(dx * dx + dy * dy);
    if (perpLen === 0)
        return { x: mx, y: my };
    const sign = large !== sweep ? 1 : -1;
    return {
        x: mx + (-dy / perpLen) * arm * sign,
        y: my + (dx / perpLen) * arm * sign,
    };
}
function midpt(a, b) {
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}
function approximateCubicBezier(a, b, c, d, tol) {
    const out = [];
    const stack = [[a, b, c, d, 0]];
    while (stack.length > 0) {
        const [q0, q1, q2, q3, depth] = stack.pop();
        if (depth > 12) {
            out.push(q0);
            continue;
        }
        const cx = q3.x - q0.x;
        const cy = q3.y - q0.y;
        const e1 = Math.abs((q1.x - q3.x) * cy - (q1.y - q3.y) * cx);
        const e2 = Math.abs((q2.x - q3.x) * cy - (q2.y - q3.y) * cx);
        if ((e1 + e2) * (e1 + e2) <= tol * tol * (cx * cx + cy * cy)) {
            out.push(q0);
        }
        else {
            const ab = midpt(q0, q1);
            const bc = midpt(q1, q2);
            const cd = midpt(q2, q3);
            const abc = midpt(ab, bc);
            const bcd = midpt(bc, cd);
            const split = midpt(abc, bcd);
            stack.push([split, bcd, cd, q3, depth + 1]);
            stack.push([q0, ab, abc, split, depth + 1]);
        }
    }
    out.push(d);
    return out;
}
function parseSvgPathToLineChains(data, relOrigin, scaleFn, forceClosed) {
    const chains = [];
    const tok = new PathTokenizer(data);
    let pen = { x: 0, y: 0 };
    let chain = { points: [], closed: false, arcs: [] };
    const xform = (pt) => ({
        x: scaleFn(pt.x - relOrigin.x),
        y: scaleFn(pt.y - relOrigin.y),
    });
    const flush = (minPts, close) => {
        if (chain.points.length >= minPts) {
            if (close)
                chain.closed = true;
            chains.push(chain);
        }
        chain = { points: [], closed: false, arcs: [] };
    };
    while (tok.hasMore) {
        const cmd = tok.take();
        switch (cmd) {
            case "M": {
                const pt = tok.xy();
                flush(2, forceClosed);
                chain.points.push(xform(pt));
                pen = pt;
                break;
            }
            case "Z": {
                flush(3, true);
                break;
            }
            case "L": {
                while (tok.peekNumeric()) {
                    const pt = tok.xy();
                    chain.points.push(xform(pt));
                    pen = pt;
                }
                break;
            }
            case "A": {
                while (tok.peekNumeric()) {
                    const rx = tok.float();
                    tok.float();
                    tok.float();
                    const largeFlag = tok.float() === 1;
                    const sweepFlag = tok.float() === 1;
                    const endPt = tok.xy();
                    const center = solveArcCenter(pen, endPt, rx, largeFlag, sweepFlag);
                    chain.arcs.push({
                        start: xform(pen),
                        end: xform(endPt),
                        center: xform(center),
                        clockwise: sweepFlag,
                    });
                    chain.points.push(xform(endPt));
                    pen = endPt;
                }
                break;
            }
            case "C": {
                const c1 = tok.xy();
                const c2 = tok.xy();
                const ep = tok.xy();
                const poly = approximateCubicBezier(xform(pen), xform(c1), xform(c2), xform(ep), 5);
                for (let k = 1; k < poly.length; k++) {
                    chain.points.push(poly[k]);
                }
                pen = ep;
                break;
            }
            default: {
                if (cmd.length > 0 && isNumericChar(cmd.charCodeAt(0))) {
                    const x = parseFloat(cmd);
                    const y = tok.float();
                    const pt = { x, y };
                    chain.points.push(xform(pt));
                    pen = pt;
                    while (tok.peekNumeric()) {
                        const lpt = tok.xy();
                        chain.points.push(xform(lpt));
                        pen = lpt;
                    }
                }
                break;
            }
        }
    }
    flush(2, forceClosed);
    return chains;
}
function parsePinPathData(lineData) {
    const regex = /^M\s*([-\d.]+)[,\s]([-\d.]+)\s*([hv])\s*([-\d.]+)\s*$/;
    const match = lineData.match(regex);
    if (!match || match.length < 5) {
        return undefined;
    }
    const startX = parseFloat(match[1]);
    const startY = parseFloat(match[2]);
    const direction = match[3];
    const length = parseFloat(match[4]);
    return {
        startPoint: { x: startX, y: startY },
        vertical: direction === "v",
        pinLen: length,
    };
}
//# sourceMappingURL=svgPathParser.js.map